/** One triangle of a business's mark, in a 44×44 viewBox. */
export interface Facet {
  points: string;
  fill: string;
}

/** Eight muted colours, each as four shades from light to deep. */
export const FACET_PALETTE: ReadonlyArray<readonly string[]> = [
  ['#dfe6f5', '#6b8cd6', '#4e69a9', '#2b3f73'], // slate blue
  ['#d4eeea', '#3fb3a6', '#2b8d82', '#135e57'], // teal
  ['#e2e0f7', '#8b80e0', '#675eb8', '#3b3486'], // indigo
  ['#e0ebdd', '#7fae84', '#5e8763', '#36573a'], // sage
  ['#f5e8cf', '#d9a54a', '#ae8031', '#7a5212'], // amber
  ['#f5dfe3', '#e07d92', '#b4586b', '#7f2a3c'], // rose
  ['#ecdff0', '#b07cc6', '#8b589d', '#5d2d6b'], // plum
  ['#dfe5ea', '#8aa0b6', '#63788d', '#34475a'], // steel
];

const EDGES = ['0,0 44,0', '44,0 44,44', '44,44 0,44', '0,44 0,0'];

/** mulberry32: integer-only maths, so every browser draws the same mark. */
function seeded(seed: number): () => number {
  let a = Math.imul(seed, 2654435761) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const drawn = new Map<number, Facet[]>();

/**
 * C29: a business's picture (picker rows, franchise header), drawn from its tenant id — four shades of
 * one colour meeting at an off-centre point. Same id, same mark, so a business
 * keeps its look across switches. Cached so change detection gets the same
 * array back and ngFor leaves the DOM alone.
 */
export function facetMark(tenantId: number): Facet[] {
  let facets = drawn.get(tenantId);
  if (!facets) {
    const random = seeded(tenantId);
    const shades = [...FACET_PALETTE[Math.floor(random() * FACET_PALETTE.length)]];
    const point = `${(12 + random() * 20).toFixed(1)},${(12 + random() * 20).toFixed(1)}`;
    for (let i = shades.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [shades[i], shades[j]] = [shades[j], shades[i]];
    }
    facets = EDGES.map((edge, i) => ({ points: `${edge} ${point}`, fill: shades[i] }));
    drawn.set(tenantId, facets);
  }
  return facets;
}
